/**
 * ApprovalActions Component (Coordinator)
 *
 * Lightweight coordinator that routes to either button or menu variant.
 * Refactored in Phase 4 - split from 379 lines to ~50 lines coordinator pattern.
 *
 * Architecture:
 * - Pure coordinator - no business logic
 * - Routes to ApprovalActionsButtons or ApprovalActionsMenu based on variant prop
 * - Delegates all state management and mutations to variant components
 *
 * Split Components:
 * - ApprovalActionsButtons: Button variant (~100 lines)
 * - ApprovalActionsMenu: Menu variant (~100 lines)
 * - RejectionDialog: Reusable dialog (~80 lines)
 *
 * Benefits:
 * - Single Responsibility Principle - each component has one job
 * - Better testability - test variants independently
 * - Easier maintenance - changes are isolated
 * - Reusability - RejectionDialog can be used elsewhere
 *
 * Usage:
 * ```tsx
 * // Button variant (default)
 * <ApprovalActions
 *   faq={faq}
 *   canApprove={isModerator}
 *   canReject={isModerator}
 *   canWithdraw={isOwner}
 *   onSuccess={showSuccess}
 *   onError={showError}
 * />
 *
 * // Menu variant (compact)
 * <ApprovalActions
 *   faq={faq}
 *   canApprove={isModerator}
 *   variant="menu"
 *   size="small"
 *   onSuccess={showSuccess}
 *   onError={showError}
 * />
 * ```
 */

import React from 'react';
import { FAQDocumentResponse } from '../../api/data-contracts';
import { ApprovalActionsButtons } from './ApprovalActionsButtons';
import { ApprovalActionsMenu } from './ApprovalActionsMenu';

export interface ApprovalActionsProps {
  faq: FAQDocumentResponse;
  canApprove?: boolean;
  canReject?: boolean;
  canWithdraw?: boolean;
  canDelete?: boolean;
  variant?: 'buttons' | 'menu';
  size?: 'small' | 'medium' | 'large';
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  onDelete?: (faq: FAQDocumentResponse) => void;
}

/**
 * ApprovalActions Coordinator
 *
 * Routes to the appropriate variant component based on the variant prop.
 * No state management or business logic - pure coordination.
 */
export const ApprovalActions: React.FC<ApprovalActionsProps> = ({
  faq,
  canApprove = false,
  canReject = false,
  canWithdraw = false,
  canDelete = false,
  variant = 'buttons',
  size = 'small',
  onSuccess,
  onError,
  onDelete,
}) => {
  // Route to appropriate variant component
  if (variant === 'menu') {
    return (
      <ApprovalActionsMenu
        faq={faq}
        canApprove={canApprove}
        canReject={canReject}
        canWithdraw={canWithdraw}
        canDelete={canDelete}
        size={size}
        onSuccess={onSuccess}
        onError={onError}
        onDelete={onDelete}
      />
    );
  }

  return (
    <ApprovalActionsButtons
      faq={faq}
      canApprove={canApprove}
      canReject={canReject}
      canWithdraw={canWithdraw}
      canDelete={canDelete}
      size={size}
      onSuccess={onSuccess}
      onError={onError}
      onDelete={onDelete}
    />
  );
};
